/**
 * Scout chat view — renders inside ChatPanel when the user selects Agent
 * Scout. Owns Scout's managed-agent session via `useManagedAgentChat`,
 * hydrates `useAdsResearchStore` via the scout_phase + tool_result handlers,
 * and consumes a queued `pendingScoutResearchRun` from the workspace store
 * to dispatch the initial research turn.
 *
 * Mirrors `SubAgentChatView` for Banan/Vid/etc., but Scout has its own
 * scout-specific seeding (`scoutSeed`, `onScoutPhase`) so we keep it as a
 * separate component instead of folding the special cases into the generic
 * sub-agent view.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { KeyboardEvent } from "react";
import type { UIMessage } from "@ai-sdk/react";
import { ChatMessageList } from "../agent-chat/ChatMessageList";
import { CompanionInput } from "../agent-chat/CompanionInput";
import {
  ThinkingBubble,
  TurnCompletedPill,
  useTurnTimer,
} from "../TurnIndicator";
import { useManagedAgentChat } from "../../hooks/useManagedAgentChat";
import { getAccessToken, resolveApiUrl } from "@/lib/jwt-auth-client";
import { useAdsWorkspaceStore } from "@/features/ads";
import {
  useAdsResearchStore,
  applyScoutPhaseEvent,
  applyScoutToolResult,
  buildScoutResearchMessage,
  type ScoutPhaseEvent,
} from "@/features/ads";

async function fetchSessionHistory(sessionId: string): Promise<UIMessage[]> {
  try {
    const token = await getAccessToken();
    const res = await fetch(resolveApiUrl(`/core/agent/managed-chat/history/${sessionId}`), {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) return [];
    const data = await res.json();
    const messages = data?.messages ?? data;
    return Array.isArray(messages)
      ? (messages.map((m) => ({ id: m.id, role: m.role, parts: m.parts })) as UIMessage[])
      : [];
  } catch {
    return [];
  }
}

export function ScoutChatView() {
  const workspaceId = useAdsWorkspaceStore((s) => s.scopeWorkspaceId);
  const pendingRun = useAdsWorkspaceStore((s) => s.pendingScoutResearchRun);
  const clearPendingRun = useAdsWorkspaceStore((s) => s.clearPendingScoutResearchRun);

  // Pick the session to attach to: prefer the artifact-bound session for the
  // currently active research brief, fall back to the global active scout
  // session id.
  const initialSessionId = useAdsResearchStore((s) => {
    const briefId = s.researchBriefId;
    if (briefId && s.scoutSessionIdsByArtifactId[briefId]) {
      return s.scoutSessionIdsByArtifactId[briefId];
    }
    return s.activeScoutSessionId;
  });
  const activeArtifactId = useAdsResearchStore((s) => s.researchBriefId);

  const [inputValue, setInputValue] = useState("");
  const [isRestoringHistory, setIsRestoringHistory] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const consumedRequestIdsRef = useRef<Set<string>>(new Set());
  const historyFetchedForRef = useRef<string | null>(null);
  const liveSessionIdRef = useRef<string | null>(initialSessionId);

  const scoutSeed = useMemo(() => {
    if (!workspaceId || !pendingRun) return null;
    return {
      workspaceId,
      ...(pendingRun.productId ? { productId: pendingRun.productId } : {}),
      productInput: pendingRun.productInput as unknown as Record<string, unknown>,
    };
  }, [workspaceId, pendingRun]);

  const handleToolResult = useCallback(
    (event: Parameters<typeof applyScoutToolResult>[0]) => {
      applyScoutToolResult(event, liveSessionIdRef.current);
    },
    [],
  );

  const {
    messages,
    setMessages,
    status,
    error,
    sessionId,
    sendMessage,
  } = useManagedAgentChat({
    agentId: "scout",
    title: pendingRun ? `Scout: ${pendingRun.productInput.productTitle}` : "Agent Scout",
    initialSessionId,
    onToolResult: handleToolResult,
    onScoutPhase: (event) => applyScoutPhaseEvent(event as ScoutPhaseEvent),
    scoutSeed,
  });

  const liveSessionId = sessionId ?? initialSessionId;

  useEffect(() => {
    liveSessionIdRef.current = liveSessionId;
    if (!liveSessionId) return;
    const research = useAdsResearchStore.getState();
    research.setActiveScoutSessionId(liveSessionId);
    if (activeArtifactId) {
      research.setScoutSessionForArtifact(activeArtifactId, liveSessionId);
    }
    const workspace = useAdsWorkspaceStore.getState();
    workspace.revealSubAgent("scout");
    workspace.setSubAgentSessionId("scout", liveSessionId);
  }, [liveSessionId, activeArtifactId]);

  useEffect(() => {
    if (!liveSessionId) return;
    if (messages.length > 0) return;
    if (historyFetchedForRef.current === liveSessionId) return;
    historyFetchedForRef.current = liveSessionId;
    setIsRestoringHistory(true);
    fetchSessionHistory(liveSessionId)
      .then((history) => {
        if (historyFetchedForRef.current !== liveSessionId) return;
        if (history.length === 0) return;
        setMessages((current) => (current.length === 0 ? history : current));
      })
      .finally(() => {
        if (historyFetchedForRef.current === liveSessionId) {
          setIsRestoringHistory(false);
        }
      });
  }, [liveSessionId, messages.length, setMessages]);

  useEffect(() => {
    if (!pendingRun || !workspaceId) return;
    if (consumedRequestIdsRef.current.has(pendingRun.requestId)) return;
    consumedRequestIdsRef.current.add(pendingRun.requestId);

    const research = useAdsResearchStore.getState();
    research.setStatus("running");
    research.setPhase(null);
    research.setProgress(null);
    research.setError(null);
    research.setQueryCount(null);
    research.setCollectionSuccessful(null);
    research.setDistilledResearch(null);
    research.setAdCopy(null);
    research.setUgcBrief(null);
    research.setResearchBriefId(null);
    research.setScoutVersion(null);
    research.setActiveProductId(pendingRun.productId ?? pendingRun.productInput.productId ?? null);
    research.setActiveScoutSessionId(null);
    useAdsWorkspaceStore.getState().revealSubAgent("scout");

    sendMessage(buildScoutResearchMessage(pendingRun.productInput));
    clearPendingRun(pendingRun.requestId);
  }, [pendingRun, workspaceId, sendMessage, clearPendingRun]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const isLoading = status === "submitted" || status === "streaming";
  const { elapsedMs: turnElapsedMs, lastDurationMs: lastTurnDurationMs } =
    useTurnTimer(isLoading);

  const displayMessages: UIMessage[] = useMemo(() => {
    if (messages.length > 0) return messages;
    return [
      {
        id: "scout-greeting",
        role: "assistant" as const,
        parts: [
          {
            type: "text" as const,
            text: "Pick a product on the Research tab and hit Start. I'll run the research here and we can refine the brief once the first pass lands.",
          },
        ],
      },
    ];
  }, [messages]);

  const handleSend = useCallback(() => {
    const text = inputValue.trim();
    if (!text) return;
    sendMessage(text);
    setInputValue("");
  }, [inputValue, sendMessage]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  return (
    <div className="flex flex-col h-full bg-background/30">
      {isRestoringHistory ? (
        <div className="flex-1 overflow-y-auto px-4 pb-2">
          <div className="flex flex-col gap-4 pt-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex gap-2 animate-pulse">
                <div className="w-6 h-6 rounded-full bg-muted/40 shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-3 bg-muted/40 rounded w-3/4" />
                  <div className="h-3 bg-muted/40 rounded w-1/2" />
                </div>
              </div>
            ))}
            <p className="text-xs text-muted-foreground text-center">
              Loading Scout history...
            </p>
          </div>
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto px-4 pb-2 ds-scrollbar min-h-0">
          <div className="flex flex-col gap-2 pt-2">
            <ChatMessageList
              messages={displayMessages}
              persona={{ humanName: "Agent Scout" }}
              compact
              mediaAutoPreview={false}
            />
            {isLoading ? (
              <ThinkingBubble name="Agent Scout" elapsedMs={turnElapsedMs} />
            ) : lastTurnDurationMs != null && messages.length > 0 ? (
              <TurnCompletedPill ms={lastTurnDurationMs} />
            ) : null}
            <div ref={chatEndRef} />
          </div>
        </div>
      )}

      {error && (
        <div className="shrink-0 px-4 py-2 bg-red-500/10 text-red-400 text-xs">
          {error.message}
        </div>
      )}

      <div className="shrink-0 bg-card/30 border-t border-border/10">
        <CompanionInput
          inputRef={inputRef}
          inputValue={inputValue}
          onInputChange={setInputValue}
          onSend={handleSend}
          onKeyDown={handleKeyDown}
          isLoading={isLoading}
          compact
          placeholder="Ask Scout to refine the research..."
          showContextPicker={false}
          showMicButton={false}
        />
      </div>
    </div>
  );
}
